import React from "react";

import lineLogo from "./LineLogo.png";

import type {
  CornerDotType,
  CornerSquareType,
  DotType,
  ErrorCorrectionLevel,
  Gradient,
  GradientType,
  Options,
} from "qr-code-styling";

/* ------------------------------------------------------------------------- */
/*                                   Types                                   */
/* ------------------------------------------------------------------------- */

export type GradientState = {
  type: GradientType;
  rotationDeg: number;
  start: string;
  end: string;
};

export type QRDesignState = {
  data: string;
  // colors
  colorMode: "single" | "gradient";
  fgColor: string;
  gradient: GradientState;
  bgColor: string;
  bgTransparent: boolean;
  customEyeColor: boolean;
  eyeFrameColor: string;
  eyeBallColor: string;
  // logo
  logo: string | null;
  logoName: string;
  hideBackgroundDots: boolean;
  logoSize: number;
  // design
  bodyShape: DotType;
  eyeFrameShape: CornerSquareType;
  eyeBallShape: CornerDotType;
  // export
  exportSize: number;
};

export const DEFAULT_QR_STATE: QRDesignState = {
  data: "",
  colorMode: "single",
  fgColor: "#06C755",
  gradient: { type: "linear", rotationDeg: 0, start: "#0F3B68", end: "#12A5B8" },
  bgColor: "#FFFFFF",
  bgTransparent: false,
  customEyeColor: false,
  eyeFrameColor: "#0F3B68",
  eyeBallColor: "#0F3B68",
  logo: lineLogo,
  logoName: "LineLogo.png",
  hideBackgroundDots: true,
  logoSize: 0.4,
  bodyShape: "square",
  eyeFrameShape: "square",
  eyeBallShape: "square",
  exportSize: 2048,
};

export const PREVIEW_SIZE = 300;

export const EXPORT_SIZE_OPTIONS = [512, 1024, 2048, 4096];

/* ------------------------------------------------------------------------- */
/*                          Thumbnail SVG primitives                         */
/* ------------------------------------------------------------------------- */

/** Rectangle path with independent corner radii (tl, tr, br, bl). */
const roundedRectPath = (
  x: number,
  y: number,
  size: number,
  tl: number,
  tr: number,
  br: number,
  bl: number
): string =>
  [
    `M ${x + tl} ${y}`,
    `H ${x + size - tr}`,
    tr ? `A ${tr} ${tr} 0 0 1 ${x + size} ${y + tr}` : "",
    `V ${y + size - br}`,
    br ? `A ${br} ${br} 0 0 1 ${x + size - br} ${y + size}` : "",
    `H ${x + bl}`,
    bl ? `A ${bl} ${bl} 0 0 1 ${x} ${y + size - bl}` : "",
    `V ${y + tl}`,
    tl ? `A ${tl} ${tl} 0 0 1 ${x + tl} ${y}` : "",
    "Z",
  ]
    .filter(Boolean)
    .join(" ");

const cornerRadii = (size: number, ratio: number): [number, number, number, number] => {
  const r = size * ratio;

  return [r, r, r, r];
};

/** One QR module drawn in the style of the given dot type. */
const renderModule = (type: DotType, key: string, x: number, y: number, size = 1): JSX.Element => {
  const half = size / 2;

  switch (type) {
    case "dots": {
      return <circle key={key} cx={x + half} cy={y + half} r={half} />;
    }
    case "rounded": {
      return <path key={key} d={roundedRectPath(x, y, size, ...cornerRadii(size, 0.25))} />;
    }
    case "extra-rounded": {
      return <path key={key} d={roundedRectPath(x, y, size, ...cornerRadii(size, 0.45))} />;
    }
    case "classy": {
      const r = size * 0.5;

      return <path key={key} d={roundedRectPath(x, y, size, r, 0, r, 0)} />;
    }
    case "classy-rounded": {
      const r = size * 0.5;
      const s = size * 0.18;

      return <path key={key} d={roundedRectPath(x, y, size, r, s, r, s)} />;
    }
    default: {
      return <rect key={key} x={x} y={y} width={size} height={size} />;
    }
  }
};

const Thumbnail = (props: { size: number; children: React.ReactNode }): JSX.Element => (
  <svg
    height={36}
    viewBox={`0 0 ${props.size} ${props.size}`}
    width={36}
    xmlns="http://www.w3.org/2000/svg"
  >
    <g fill="currentColor">{props.children}</g>
  </svg>
);

/* --------------------------------- Body ---------------------------------- */

// Fixed pseudo-QR pattern so every body thumbnail is comparable.
const BODY_PATTERN = [
  [1, 0, 1, 1, 0],
  [0, 1, 1, 0, 1],
  [1, 1, 0, 1, 1],
  [0, 1, 1, 1, 0],
  [1, 0, 1, 0, 1],
];

const bodyThumbnail = (type: DotType): JSX.Element => (
  <Thumbnail size={5}>
    {BODY_PATTERN.flatMap((row, y) =>
      row.map((cell, x) => (cell ? renderModule(type, `${x}-${y}`, x, y) : null)).filter(Boolean)
    )}
  </Thumbnail>
);

/* ------------------------------- Eye frame -------------------------------- */

// Types drawn natively by qr-code-styling (see constants/cornerSquareTypes).
const NATIVE_EYE_FRAME_TYPES = ["square", "dot", "extra-rounded"];

// Types drawn natively by qr-code-styling (see constants/cornerDotTypes).
const NATIVE_EYE_BALL_TYPES = ["square", "dot"];

const eyeFrameThumbnail = (type: CornerSquareType): JSX.Element => {
  if (NATIVE_EYE_FRAME_TYPES.includes(type)) {
    const common = { fill: "none", stroke: "currentColor", strokeWidth: 1 };

    return (
      <Thumbnail size={7}>
        {type === "dot" ? (
          <circle cx={3.5} cy={3.5} r={3} {...common} />
        ) : (
          <rect
            height={6}
            rx={type === "extra-rounded" ? 2 : 0}
            width={6}
            x={0.5}
            y={0.5}
            {...common}
          />
        )}
      </Thumbnail>
    );
  }

  // "dots" keeps its modules separated, the rest merge into a continuous frame.
  if (type === "dots") {
    const modules: JSX.Element[] = [];

    for (let y = 0; y < 7; y += 1) {
      for (let x = 0; x < 7; x += 1) {
        if (x === 0 || y === 0 || x === 6 || y === 6) {
          modules.push(renderModule("dots", `${x}-${y}`, x, y));
        }
      }
    }

    return <Thumbnail size={7}>{modules}</Thumbnail>;
  }

  const [tl, tr, br, bl] =
    type === "classy"
      ? [3, 0, 3, 0]
      : type === "classy-rounded"
        ? [3, 1, 3, 1]
        : cornerRadii(6, 0.25);

  return (
    <Thumbnail size={7}>
      <path
        d={roundedRectPath(0.5, 0.5, 6, tl, tr, br, bl)}
        fill="none"
        stroke="currentColor"
        strokeWidth={1}
      />
    </Thumbnail>
  );
};

/* -------------------------------- Eye ball -------------------------------- */

const eyeBallThumbnail = (type: CornerDotType): JSX.Element => {
  if (NATIVE_EYE_BALL_TYPES.includes(type)) {
    return (
      <Thumbnail size={3}>
        {type === "dot" ? (
          <circle cx={1.5} cy={1.5} r={1.5} />
        ) : (
          <rect height={3} width={3} x={0} y={0} />
        )}
      </Thumbnail>
    );
  }

  // The remaining types merge their 3x3 modules into one blob.
  const [tl, tr, br, bl] =
    type === "classy"
      ? [1.5, 0, 1.5, 0]
      : type === "classy-rounded"
        ? [1.5, 0.55, 1.5, 0.55]
        : cornerRadii(3, type === "extra-rounded" ? 0.45 : 0.25);

  return (
    <Thumbnail size={3}>
      <path d={roundedRectPath(0, 0, 3, tl, tr, br, bl)} />
    </Thumbnail>
  );
};

/* ------------------------------------------------------------------------- */
/*                             Shape option lists                            */
/* ------------------------------------------------------------------------- */

export type ShapeOption<T> = { value: T; label: string; icon: JSX.Element };

const DOT_TYPES: DotType[] = [
  "square",
  "dots",
  "rounded",
  "extra-rounded",
  "classy",
  "classy-rounded",
];

export const BODY_SHAPES: ShapeOption<DotType>[] = DOT_TYPES.map((value) => ({
  value,
  label: value,
  icon: bodyThumbnail(value),
}));

const CORNER_SQUARE_TYPES: CornerSquareType[] = ["square", "dot", "extra-rounded", ...DOT_TYPES.filter((type) => type !== "square" && type !== "extra-rounded")];

export const EYE_FRAME_SHAPES: ShapeOption<CornerSquareType>[] = CORNER_SQUARE_TYPES.map(
  (value) => ({ value, label: value, icon: eyeFrameThumbnail(value) })
);

// "dots" is deliberately excluded: it splits the eye ball into separate circles,
// which breaks the finder pattern ratio and makes the QR code unreadable.
const CORNER_DOT_TYPES: CornerDotType[] = [
  "square",
  "dot",
  ...DOT_TYPES.filter((type) => type !== "square" && type !== "dots"),
];

export const EYE_BALL_SHAPES: ShapeOption<CornerDotType>[] = CORNER_DOT_TYPES.map((value) => ({
  value,
  label: value,
  icon: eyeBallThumbnail(value),
}));

/* ------------------------------------------------------------------------- */
/*                          State -> library options                         */
/* ------------------------------------------------------------------------- */

const toGradient = (gradient: GradientState): Gradient => ({
  type: gradient.type,
  rotation: (gradient.rotationDeg * Math.PI) / 180,
  colorStops: [
    { offset: 0, color: gradient.start },
    { offset: 1, color: gradient.end },
  ],
});

/**
 * Maps the screen state onto qr-code-styling options.
 * `color` and `gradient` are mutually exclusive per section, so only one is set.
 */
export const buildQROptions = (state: QRDesignState, size: number): Options => {
  const isGradient = state.colorMode === "gradient";
  const fill = isGradient ? { gradient: toGradient(state.gradient) } : { color: state.fgColor };
  const eyeFrameFill = state.customEyeColor && !isGradient ? { color: state.eyeFrameColor } : fill;
  const eyeBallFill = state.customEyeColor && !isGradient ? { color: state.eyeBallColor } : fill;
  // A logo covers modules, so bump error correction to survive it.
  const errorCorrectionLevel: ErrorCorrectionLevel = state.logo ? "H" : "Q";

  return {
    type: "canvas",
    width: size,
    height: size,
    margin: Math.round(size * 0.04),
    data: state.data,
    image: state.logo || undefined,
    qrOptions: { errorCorrectionLevel },
    imageOptions: {
      hideBackgroundDots: state.hideBackgroundDots,
      imageSize: state.logoSize,
      margin: Math.round(size * 0.01),
    },
    dotsOptions: { type: state.bodyShape, ...fill },
    cornersSquareOptions: { type: state.eyeFrameShape, ...eyeFrameFill },
    cornersDotOptions: { type: state.eyeBallShape, ...eyeBallFill },
    backgroundOptions: { color: state.bgTransparent ? "transparent" : state.bgColor },
  };
};

/* ------------------------------------------------------------------------- */
/*                         Logo background removal                           */
/* ------------------------------------------------------------------------- */

const MAX_LOGO_DIMENSION = 512;

/**
 * Makes the logo's own backdrop transparent: flood fills inward from the image
 * edges, clearing every pixel within `tolerance` of the seeded edge colour.
 * Flood filling (instead of a global colour match) keeps same-coloured pixels
 * that are enclosed by the artwork.
 */
export const removeLogoBackground = (dataUrl: string, tolerance = 40): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();

    image.addEventListener("error", () => {
      reject(new Error("Cannot read the uploaded image"));
    });

    image.addEventListener("load", () => {
      const scale = Math.min(1, MAX_LOGO_DIMENSION / Math.max(image.width, image.height));
      const width = Math.max(1, Math.round(image.width * scale));
      const height = Math.max(1, Math.round(image.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext("2d");

      if (!context) {
        reject(new Error("Cannot create a canvas context"));

        return;
      }

      context.drawImage(image, 0, 0, width, height);

      const imageData = context.getImageData(0, 0, width, height);
      const pixels = imageData.data;
      const visited = new Uint8Array(width * height);
      const stack: number[] = [];

      const seed = (x: number, y: number): void => {
        const index = y * width + x;

        if (!visited[index]) {
          visited[index] = 1;
          stack.push(index);
        }
      };

      for (let x = 0; x < width; x += 1) {
        seed(x, 0);
        seed(x, height - 1);
      }

      for (let y = 0; y < height; y += 1) {
        seed(0, y);
        seed(width - 1, y);
      }

      // Reference colour = the image's top-left pixel.
      const [refR, refG, refB] = [pixels[0], pixels[1], pixels[2]];
      const matches = (offset: number): boolean =>
        pixels[offset + 3] === 0 ||
        (Math.abs(pixels[offset] - refR) <= tolerance &&
          Math.abs(pixels[offset + 1] - refG) <= tolerance &&
          Math.abs(pixels[offset + 2] - refB) <= tolerance);

      while (stack.length > 0) {
        const index = stack.pop() as number;
        const offset = index * 4;

        if (!matches(offset)) {
          continue;
        }

        pixels[offset + 3] = 0;

        const x = index % width;
        const y = Math.floor(index / width);

        if (x > 0) seed(x - 1, y);
        if (x < width - 1) seed(x + 1, y);
        if (y > 0) seed(x, y - 1);
        if (y < height - 1) seed(x, y + 1);
      }

      context.putImageData(imageData, 0, 0);

      resolve(canvas.toDataURL("image/png"));
    });

    image.src = dataUrl;
  });
